import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Search, Map, Navigation, Trash2, Loader2, RefreshCw, KeyRound, Image as ImageIcon, Copy, X, Download, Camera, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import MapPreviewModal from "../../components/MapPreviewModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCustomerLocations() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [showPhoto, setShowPhoto] = useState(null); // location object
  const [generatedCode, setGeneratedCode] = useState(null); // {customer_id, code, expires_at}
  const [showMapView, setShowMapView] = useState(false);

  const adminToken = localStorage.getItem("admin_token");

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (sourceFilter) params.source = sourceFilter;
      const res = await axios.get(`${API}/admin/customer-locations`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params,
      });
      setLocations(res.data?.locations || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      // Do NOT auto-navigate on 401. Admin only logs out via "Keluar" button.
      if (err?.response?.status === 401) {
        toast.error("Gagal memuat lokasi pelanggan (unauthorized).");
      } else {
        toast.error("Gagal memuat lokasi pelanggan.");
      }
    } finally {
      setLoading(false);
    }
  }, [adminToken, search, sourceFilter, navigate]);

  useEffect(() => {
    if (!adminToken) { navigate("/admin/login"); return; }
    fetchLocations();
  }, [fetchLocations, adminToken, navigate]);

  const handleDelete = async (customerId) => {
    if (!window.confirm(`Hapus lokasi (& foto) pelanggan ${customerId}?`)) return;
    try {
      await axios.delete(`${API}/admin/customer-locations/${encodeURIComponent(customerId)}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success("Lokasi dihapus.");
      fetchLocations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus lokasi.");
    }
  };

  const handleDeletePhoto = async (customerId) => {
    if (!window.confirm(`Hapus foto rumah pelanggan ${customerId}? (GPS tetap)`)) return;
    try {
      await axios.delete(`${API}/admin/customer-locations/${encodeURIComponent(customerId)}/photo`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success("Foto dihapus.");
      fetchLocations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus foto.");
    }
  };

  const handleGenerateCode = async (customerId, type = "photo") => {
    try {
      let endpoint;
      if (type === "gps") {
        endpoint = `${API}/admin/customer-locations/${encodeURIComponent(customerId)}/generate-gps-update-code`;
      } else if (type === "whatsapp") {
        endpoint = `${API}/admin/customer-locations/${encodeURIComponent(customerId)}/generate-whatsapp-update-code`;
      } else {
        endpoint = `${API}/admin/customer-locations/${encodeURIComponent(customerId)}/generate-photo-update-code`;
      }
      const res = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setGeneratedCode({ customer_id: customerId, code: res.data.code, expires_at: res.data.expires_at, type });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal generate kode.");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Kode ${code} disalin.`);
  };

  const openMaps = (loc) => window.open(
    `https://www.google.com/maps/place/${loc.latitude},${loc.longitude}/@${loc.latitude},${loc.longitude},20z/data=!3m1!1e3`,
    "_blank", "noopener,noreferrer"
  );
  const openNav = (loc) => window.open(
    `https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}&travelmode=driving&layer=satellite`,
    "_blank", "noopener,noreferrer"
  );

  const handleSearch = (e) => { e.preventDefault(); fetchLocations(); };

  const downloadPhoto = (loc) => {
    if (!loc?.photo) return;
    const a = document.createElement("a");
    a.href = loc.photo;
    a.download = `Rumah_${loc.customer_id}.jpg`;
    a.click();
  };

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-customer-locations">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-cyan-600" />
              Lokasi & Foto Pelanggan
            </h1>
            <p className="text-sm text-slate-500">GPS rumah pelanggan + foto rumah yang di-lock oleh petugas.</p>
          </div>
          <div className="text-sm text-slate-600">
            Total tersimpan: <span className="font-bold text-cyan-700">{total}</span>
          </div>
        </div>

        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Cari berdasarkan ID, nama, atau alamat..." className="pl-9"
                  value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" />
              </div>
              <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="source-filter">
                <option value="">Semua Sumber</option>
                <option value="PDAM">PDAM</option>
                <option value="PDAM_TEBO">PDAM Tebo</option>
              </select>
              <Button type="submit" className="gap-1" data-testid="search-btn"><Search className="w-4 h-4" /> Cari</Button>
              <Button type="button" variant="outline" onClick={fetchLocations} className="gap-1" data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh</Button>
              <Button type="button" variant="outline"
                className="gap-1 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                onClick={() => {
                  const withCoords = (locations || []).filter(l => l.latitude != null && l.longitude != null);
                  if (withCoords.length === 0) {
                    toast.error("Tidak ada lokasi dengan koordinat untuk ditampilkan di peta.");
                    return;
                  }
                  setShowMapView(true);
                }}
                data-testid="open-map-view-btn">
                <Map className="w-4 h-4" /> Peta Semua
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 flex items-center justify-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Memuat...
              </div>
            ) : locations.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <MapPin className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>Belum ada lokasi pelanggan tersimpan.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-3 font-semibold text-slate-700">Foto</th>
                      <th className="text-left p-3 font-semibold text-slate-700">ID Pelanggan</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Nama</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Alamat</th>
                      <th className="text-left p-3 font-semibold text-slate-700">WhatsApp</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Sumber</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Koordinat</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Diperbarui</th>
                      <th className="text-right p-3 font-semibold text-slate-700">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((loc) => (
                      <tr key={loc.customer_id} className="border-b border-slate-100 hover:bg-slate-50/60"
                        data-testid={`location-row-${loc.customer_id}`}>
                        <td className="p-3">
                          {loc.photo ? (
                            <button onClick={() => setShowPhoto(loc)} className="block rounded overflow-hidden border border-slate-200 hover:border-cyan-400">
                              <img src={loc.photo} alt="Rumah" className="w-16 h-16 object-cover" />
                            </button>
                          ) : (
                            <div className="w-16 h-16 rounded border border-dashed border-slate-200 flex items-center justify-center text-slate-300">
                              <Camera className="w-5 h-5" />
                            </div>
                          )}
                        </td>
                        <td className="p-3 font-mono text-xs">{loc.customer_id}</td>
                        <td className="p-3">{loc.customer_name || "-"}</td>
                        <td className="p-3 max-w-[180px] truncate" title={loc.customer_address || ""}>{loc.customer_address || "-"}</td>
                        <td className="p-3 text-xs">
                          {loc.whatsapp_number ? (
                            <a href={`https://wa.me/${loc.whatsapp_number}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366]/20 font-mono">
                              <MessageCircle className="w-3 h-3" /> +{loc.whatsapp_number}
                            </a>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-cyan-100 text-cyan-700">
                            {loc.source === "PDAM_TEBO" ? "PDAM Tebo" : "PDAM"}
                          </span>
                        </td>
                        <td className="p-3 font-mono text-xs">
                          {loc.latitude?.toFixed(6)}, {loc.longitude?.toFixed(6)}
                          {loc.accuracy != null && <span className="block text-[10px] text-slate-400">±{Math.round(loc.accuracy)} m</span>}
                        </td>
                        <td className="p-3 text-xs text-slate-600">
                          {loc.updated_at ? new Date(loc.updated_at).toLocaleString("id-ID") : "-"}
                          <span className="block text-[10px] text-slate-400">oleh {loc.updated_by || "-"}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1 flex-wrap">
                            <Button size="sm" variant="outline" className="gap-1 border-cyan-300 text-cyan-700"
                              onClick={() => openMaps(loc)} data-testid={`view-${loc.customer_id}`}>
                              <Map className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" className="gap-1 border-emerald-300 text-emerald-700"
                              onClick={() => openNav(loc)} data-testid={`nav-${loc.customer_id}`}>
                              <Navigation className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" className="gap-1 border-purple-300 text-purple-700"
                              onClick={() => handleGenerateCode(loc.customer_id, "gps")}
                              data-testid={`gencode-gps-${loc.customer_id}`}
                              title="Generate kode update GPS">
                              <KeyRound className="w-3.5 h-3.5" /> GPS
                            </Button>
                            {loc.whatsapp_number && (
                              <Button size="sm" variant="outline" className="gap-1 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                                onClick={() => handleGenerateCode(loc.customer_id, "whatsapp")}
                                data-testid={`gencode-wa-${loc.customer_id}`}
                                title="Generate kode update No. WA">
                                <KeyRound className="w-3.5 h-3.5" /> WA
                              </Button>
                            )}
                            {loc.photo && (
                              <>
                                <Button size="sm" variant="outline" className="gap-1 border-amber-300 text-amber-700"
                                  onClick={() => handleGenerateCode(loc.customer_id, "photo")}
                                  data-testid={`gencode-${loc.customer_id}`}
                                  title="Generate kode update foto">
                                  <KeyRound className="w-3.5 h-3.5" /> Foto
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 border-orange-300 text-orange-700"
                                  onClick={() => handleDeletePhoto(loc.customer_id)}
                                  data-testid={`delphoto-${loc.customer_id}`}
                                  title="Hapus foto saja">
                                  <ImageIcon className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="outline" className="gap-1 border-red-300 text-red-700"
                              onClick={() => handleDelete(loc.customer_id)} data-testid={`delete-${loc.customer_id}`}
                              title="Hapus seluruh data lokasi">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Map view of all saved locations (Fit All enabled for admin) */}
      {showMapView && (() => {
        const withCoords = (locations || []).filter(l => l.latitude != null && l.longitude != null);
        if (withCoords.length === 0) return null;
        const center = withCoords[0];
        return (
          <MapPreviewModal
            open={true}
            initialLat={center.latitude}
            initialLng={center.longitude}
            initialAccuracy={null}
            onCancel={() => setShowMapView(false)}
            title="Peta Semua Lokasi Pelanggan"
            subtitle={`${withCoords.length} pelanggan tertandai`}
            readonly={true}
            nearby={withCoords.map(l => ({
              customer_id: l.customer_id,
              customer_name: l.customer_name,
              customer_address: l.customer_address,
              latitude: l.latitude,
              longitude: l.longitude,
              photo: l.photo,
              photo_updated_at: l.photo_updated_at,
              updated_at: l.updated_at,
            }))}
            enableFitAll={true}
            onDetailClick={(n) => setShowPhoto(n)}
          />
        );
      })()}

      {/* Photo modal */}
      {showPhoto && (
        <div className="fixed inset-0 z-[140] bg-black/85 flex items-center justify-center p-4" onClick={() => setShowPhoto(null)} data-testid="photo-modal">
          <div className="relative max-w-3xl w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <button className="absolute -top-2 -right-2 bg-white rounded-full p-1.5 shadow-lg z-10" onClick={() => setShowPhoto(null)}>
              <X className="w-5 h-5 text-slate-700" />
            </button>
            {showPhoto.photo ? (
              <img src={showPhoto.photo} alt="Foto rumah" className="w-full h-auto max-h-[80vh] object-contain rounded-lg shadow-2xl bg-white" />
            ) : (
              <div className="w-full bg-white rounded-lg shadow-2xl p-10 flex flex-col items-center justify-center text-slate-400 min-h-[260px]">
                <Camera className="w-12 h-12 mb-2" />
                <p className="text-sm">Belum ada foto rumah tersimpan</p>
              </div>
            )}
            <div className="mt-3 flex items-center justify-between gap-2 text-white text-xs flex-wrap">
              <div>
                <div className="font-semibold text-sm flex items-center gap-2">
                  {showPhoto.customer_name || showPhoto.customer_id}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/90 text-white text-[10px] font-semibold">
                    Aktif
                  </span>
                </div>
                {showPhoto.customer_address && <div className="opacity-80">{showPhoto.customer_address}</div>}
                {showPhoto.photo_updated_at && (
                  <div className="opacity-70">Foto diperbarui: {new Date(showPhoto.photo_updated_at).toLocaleString("id-ID")}</div>
                )}
              </div>
              {showPhoto.photo && (
                <Button size="sm" className="gap-1 bg-white text-slate-800 hover:bg-slate-100" onClick={() => downloadPhoto(showPhoto)}>
                  <Download className="w-3.5 h-3.5" /> Download
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generated code modal */}
      {generatedCode && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="generated-code-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className={`text-white p-4 text-center ${
              generatedCode.type === "gps" ? "bg-purple-600" :
              generatedCode.type === "whatsapp" ? "bg-[#25D366]" : "bg-amber-500"
            }`}>
              <KeyRound className="w-8 h-8 mx-auto mb-1" />
              <h3 className="font-bold">Kode Update {
                generatedCode.type === "gps" ? "Lokasi GPS" :
                generatedCode.type === "whatsapp" ? "No. WhatsApp" : "Foto"
              }</h3>
              <p className="text-xs opacity-90">Untuk pelanggan: {generatedCode.customer_id}</p>
            </div>
            <div className="p-4 space-y-3 text-center">
              <div className="text-3xl font-bold tracking-widest text-amber-600 font-mono select-all">
                {generatedCode.code}
              </div>
              <p className="text-xs text-slate-500">Berlaku hingga {new Date(generatedCode.expires_at).toLocaleTimeString("id-ID")} (15 menit)</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 gap-1" onClick={() => copyCode(generatedCode.code)}>
                  <Copy className="w-4 h-4" /> Salin
                </Button>
                <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setGeneratedCode(null)}>
                  Tutup
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
